import { type RefObject, useCallback, type Dispatch, type SetStateAction, useState } from 'react';

import DoneIcon from '@mui/icons-material/Done';
import { Popover, Typography } from '@mui/material';
import { type RowSelectionState } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { type useRecordingDialogs } from './useRecordingDialogs';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { useTransmitUserDetails } from '../../hooks/useTransmission';
import { type ArtifactType, RecordingStatus } from '../../types/recordingTypes';
import { copyToClipboard } from '../../utils/clipboard';
import { getUrls } from '../../utils/url';
import { type ClassRecording } from '../classroom/types';
import { type RecordingDialogProps, type ClassRecordingExtended } from '../classroom/ui-types';
import { getRecordingArtifact, getRecordingInfo } from '../classroom/util';
import { type ActionMenuItem } from '../common/table/ActionMenu';

const StyledPopover = styled(Popover)`
    .MuiPopover-paper {
        background-color: ${(props) => props.theme.colors.actions.main} !important;
        color: ${(props) => props.theme.colors.white} !important;
    }
`;

type UseRecordingActionsProps = {
    userUuid: string;
    classUuid: string;
    isInstructor: boolean;
    recordings: ClassRecordingExtended[];
    setRecordings: (recordings: ClassRecordingExtended[]) => void;
    rowSelection: Record<string, boolean>;
    setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
    dialogSetters: ReturnType<typeof useRecordingDialogs>['dialogSetters'];
};

export const useRecordingActions = ({
    userUuid,
    classUuid,
    isInstructor,
    recordings,
    setRecordings,
    rowSelection,
    setRowSelection,
    dialogSetters
}: UseRecordingActionsProps) => {
    const { t } = useTranslation();

    const { data: orgSettings } = useOrgSettings();

    const { data: schoolInfo } = useSchoolInfo();
    const { transmitUserDetails } = useTransmitUserDetails();

    const [popoverCopyLinkOpen, setPopoverCopyLinkOpen] = useState(false);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<RefObject<unknown> | null>(null);

    const { enqueueSnackbar } = useSnackbar();

    const handleBulkChangePermissions = useCallback(() => {
        dialogSetters.setBulkPermissionProps((current) => ({
            ...current,
            rowSelection,
            setRowSelection,
            open: true
        }));
    }, [dialogSetters, rowSelection, setRowSelection]);

    const getRecordingLinkForRecording = useCallback(
        (recording: ClassRecording): string => {
            const url = new URL(`${getUrls().vanityUrl}/player/recording/${classUuid}/${recording.recordingId}`);
            return url.toString();
        },
        [classUuid]
    );

    const handleCopyLink = useCallback(
        async (recordingId: string, refEl?: RefObject<unknown>) => {
            const recording = recordings.find((r) => r.recordingId === recordingId);
            const url = getRecordingLinkForRecording(recording!);
            await copyToClipboard(url);
            if (refEl) {
                setPopoverAnchorEl(refEl);
            }
            setPopoverCopyLinkOpen(true);
            // Auto close popover after 2 seconds.
            window.setTimeout(() => {
                setPopoverCopyLinkOpen(false);
            }, 2000);
        },
        [recordings, getRecordingLinkForRecording]
    );

    const handleLaunch = useCallback(
        (recording: ClassRecording) => {
            if (recording.status === 'AVAILABLE') {
                transmitUserDetails(new URL(getRecordingLinkForRecording(recording)));
                return;
            }

            if (!schoolInfo) {
                throw new Error('Failed to launch - schoolInfo not available');
            }

            const setRecordingLoading = (recordingId: string, isLoading: boolean) => {
                const index = recordings.findIndex((r) => r.recordingId === recordingId);
                if (index === -1) return recordings; // Return early if not found

                const updatedRecordings = [...recordings];
                updatedRecordings[index] = { ...updatedRecordings[index], isLoading };

                setRecordings(updatedRecordings);
            };

            const launchExternal = async () => {
                setRecordingLoading(recording.recordingId, true);
                // Preffered artifact types in order of preference.
                const preferredArtifactTypes: ArtifactType[] = [
                    'composite',
                    'shared_screen_with_speaker_view(CC)',
                    'shared_screen_with_speaker_view',
                    'shared_screen_with_gallery_view',
                    'shared_screen',
                    'active_speaker',
                    'host_video',
                    'gallery_view',
                    'audio_only'
                ];

                const recordingInfo = await getRecordingInfo(
                    schoolInfo.uuid,
                    userUuid,
                    classUuid,
                    recording.recordingId
                );

                if (!recordingInfo?.artifacts) {
                    setRecordingLoading(recording.recordingId, false);
                    return;
                }

                let artifactType: ArtifactType | undefined;
                for (const p in preferredArtifactTypes) {
                    const prefType = preferredArtifactTypes[p];
                    if (Object.keys(recordingInfo.artifacts).includes(prefType)) {
                        artifactType = prefType as ArtifactType;
                        break;
                    }
                }
                if (!artifactType) {
                    console.error('Unable to find a suitable recording artifact to play');
                    enqueueSnackbar(t('lti:recordings.errors.no-suitable-artifacts'), { variant: 'error' });
                    setRecordingLoading(recording.recordingId, false);
                    return;
                }

                const artifact = await getRecordingArtifact(
                    schoolInfo.uuid,
                    userUuid,
                    classUuid,
                    recording.recordingId,
                    artifactType
                );
                if (!artifact) {
                    console.error('Failure to get recording artifact');
                    enqueueSnackbar(t('lti:recordings.errors.failure-to-get-artifact'), { variant: 'error' });
                    setRecordingLoading(recording.recordingId, false);
                    return;
                }
                setRecordingLoading(recording.recordingId, false);
                (window as any).open(artifact.url, '_blank').focus();
            };
            launchExternal();
        },
        [
            getRecordingLinkForRecording,
            transmitUserDetails,
            userUuid,
            classUuid,
            schoolInfo,
            recordings,
            setRecordings,
            t,
            enqueueSnackbar
        ]
    );

    const handleActionRefresh = useCallback(
        async (recordingId: string) => {
            const info = await getRecordingInfo(schoolInfo!.uuid!, userUuid, classUuid, recordingId);
            if (info) {
                console.log('Recording refreshed');
                setRecordings(
                    recordings.map((rec) =>
                        rec.recordingId === recordingId
                            ? { ...rec, status: info.status, duration: info.duration ?? 0 }
                            : rec
                    )
                );
            }
        },
        [schoolInfo, userUuid, classUuid, recordings, setRecordings]
    );

    const standardHandleDialogOpen = useCallback(
        (recordingId: string, setter: (prop: any) => void, propOverrides?: Partial<RecordingDialogProps>) => {
            const recording = recordings.find((r) => r.recordingId === recordingId);
            setter((current) => ({ ...current, recording, open: true, ...(propOverrides ?? {}) }));
        },
        [recordings]
    );

    const handleExternalLink = useCallback(
        (recordingId: string) =>
            standardHandleDialogOpen(recordingId, dialogSetters.setLinksProps, { downloading: false }),
        [standardHandleDialogOpen, dialogSetters]
    );

    const handleActionDownload = useCallback(
        (recordingId: string) =>
            standardHandleDialogOpen(recordingId, dialogSetters.setLinksProps, { downloading: true }),
        [standardHandleDialogOpen, dialogSetters]
    );

    const handleActionEdit = useCallback(
        (recordingId: string) => {
            setRowSelection({}); // Need to wipe this so that it knows it's in single row edit mode.
            standardHandleDialogOpen(recordingId, dialogSetters.setEditProps);
        },
        [standardHandleDialogOpen, dialogSetters, setRowSelection]
    );

    const handleActionDelete = useCallback(
        (recordingId: string) => standardHandleDialogOpen(recordingId, dialogSetters.setDeleteProps),
        [standardHandleDialogOpen, dialogSetters]
    );

    const handleActionViewAnalytics = useCallback(
        (recordingId: string) => standardHandleDialogOpen(recordingId, dialogSetters.setAnalyticsProps),
        [standardHandleDialogOpen, dialogSetters]
    );

    const getActions = (recording: ClassRecording): ActionMenuItem[] => {
        const items: ActionMenuItem[] = [{ name: t('common:refresh'), onClick: handleActionRefresh }];
        const downloadsDisabled = Boolean(orgSettings?.featureSettings.options.recordingsDisableDownloads);

        if (isInstructor) {
            items.push({ name: t('lti:recordings.edit-settings'), onClick: handleActionEdit });
        }

        if (recording.status === RecordingStatus.EXTERNAL) {
            items.push({ name: t('lti:recordings.external-link'), onClick: handleExternalLink });

            if (isInstructor) {
                items.push({ name: t('common:delete'), onClick: handleActionDelete });
            }
        } else if (recording.status === RecordingStatus.AVAILABLE) {
            items.push({ name: t('lti:recordings.copy-link'), onClick: handleCopyLink });
            if (!downloadsDisabled && (isInstructor || orgSettings?.recordingSettings.participantsCanDownload)) {
                items.push({ name: t('common:download'), onClick: handleActionDownload });
            }

            if (isInstructor) {
                items.push({ name: t('lti:recordings.view-analytics'), onClick: handleActionViewAnalytics });
                items.push({ name: t('common:delete'), onClick: handleActionDelete });
            }
        }

        return items;
    };

    const renderPopover = useCallback(
        () => (
            <StyledPopover
                id="copy-link"
                open={popoverCopyLinkOpen}
                anchorEl={popoverAnchorEl?.current as HTMLElement}
                onClose={() => setPopoverCopyLinkOpen(false)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Typography sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center' }}>
                    {t('common:copiedlink')}
                    <DoneIcon sx={{ color: '#fff', marginLeft: '5px' }} />
                </Typography>
            </StyledPopover>
        ),
        [popoverAnchorEl, popoverCopyLinkOpen, t]
    );

    return {
        handleBulkChangePermissions,
        renderPopover,
        getActions,
        handleLaunch
    };
};
